import { Theme } from "@radix-ui/themes";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "@radix-ui/themes/styles.css";
import "./index.css";

import BillingResultView from "./components/BillingResultView";
import { ErrorBoundary } from "./components/ErrorBoundary";
import DeviceVerifiedSuccessView from "./components/mcp/device/DeviceVerifiedSuccessView";
import LoadingIndicator from "./components/shared/LoadingIndicator";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SupabaseProvider } from "./contexts/SupabaseContext";
import { ThemeAppearanceProvider, useThemeAppearance } from "./contexts/ThemeAppearanceContext";
import { UsageProvider, useUsage } from "./contexts/UsageContext";
import AddDevicePage from "./pages/AddDevicePage";
import DeviceVerificationPage from "./pages/DeviceVerificationPage";
import DevicesPage from "./pages/dashboard/DevicesPage";
import PlanBillingPage from "./pages/dashboard/PlanBillingPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import UsagePage from "./pages/dashboard/UsagePage";
import { configureBrowserAnalytics, legacyPostHogPageviewUrl, trackPageview } from "./utils/browserAnalytics";
import { getPostHog, initPostHog } from "./utils/posthogConfig";
import { initSentry } from "./utils/sentryConfig";
import type { HubSpotWindow } from "./utils/supportChat";

// Initialize Sentry as early as possible
initSentry();

// Independent browser telemetry is configured even if PostHog is disabled.
configureBrowserAnalytics(import.meta.env.VITE_REMOTE_PRODUCT_TELEMETRY_ENABLED === "true");
// Initialize optional PostHog destination.
initPostHog();

const HUBSPOT_SCRIPT_ID = "hs-script-loader";
const HUBSPOT_SCRIPT_URL = "https://js-eu1.hs-scripts.com/146727725.js";

/**
 * Wrapper component that provides auth credentials to SupabaseProvider
 * This ensures session is initialized once at the app level
 */
function AuthenticatedSupabaseProvider({ children }: { children: React.ReactNode }) {
    const { accessToken, user } = useAuth();

    return (
        <SupabaseProvider accessToken={accessToken} userId={user?.id}>
            {children}
        </SupabaseProvider>
    );
}

/**
 * LegacyRedirectHandler
 *
 * Handles backward compatibility for old URL parameters.
 * Redirects:
 * - /?device_verify=true&user_code=... -> /verify-device?user_code=...
 *
 * Note: OAuth callback handling (session_id) is now handled by auth-only.tsx
 */
function LegacyRedirectHandler() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Only check redirections on the root path
        if (location.pathname !== "/") {
            return;
        }

        const deviceVerify = searchParams.get("device_verify") === "true";
        const userCode = searchParams.get("user_code");

        if (deviceVerify && userCode) {
            console.log("🔄 [ROUTER] Redirecting legacy device verification URL");
            navigate(`/verify-device?user_code=${userCode}`, {
                replace: true,
            });
        }
    }, [
        searchParams,
        navigate,
        location.pathname,
    ]);

    return null;
}

/**
 * RootRoute
 *
 * Conditionally renders Dashboard or Landing page based on authentication status.
 * Replaces separate PublicOnly/RequireAuth routes for the root path.
 */
function SignedOutRedirect() {
    useEffect(() => {
        const authUrl = new URL(import.meta.env.VITE_AUTH_URL || window.location.origin);
        authUrl.searchParams.set("mcp_dashboard", "true");
        window.location.replace(authUrl.toString());
    }, []);

    return <LoadingIndicator text="Opening login..." />;
}

function RootRoute() {
    const { user, isSessionChecked } = useAuth();

    if (!isSessionChecked) {
        return <LoadingIndicator text="Checking session..." />;
    }

    if (user) {
        // User is authenticated, show the Devices dashboard page
        return <DevicesPage />;
    }

    // The MCP domain is the dashboard entry point. Signed-out users belong on
    // the auth app, which owns login/signup and redirects back after success.
    return <SignedOutRedirect />;
}

const RequireAuth = ({ children }: { children: JSX.Element }) => {
    const { user, isSessionChecked } = useAuth();

    if (!isSessionChecked) {
        return <LoadingIndicator text="Checking session..." />;
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function DeviceVerifiedSuccessRoute() {
    const [searchParams] = useSearchParams();
    const userEmail = searchParams.get("email") || "";
    const deviceName = searchParams.get("device_name") || "";

    return <DeviceVerifiedSuccessView userEmail={userEmail} deviceName={deviceName} />;
}

function AppRoutes() {
    const location = useLocation();
    const { user } = useAuth();
    const { usage } = useUsage();

    useEffect(() => {
        // Track pageview on mount and subsequent route changes
        const event = trackPageview(); // Independent BigQuery emission.
        // Preserve the original PostHog SPA pageview capture path and URL.
        getPostHog()?.capture("$pageview", {
            $current_url: legacyPostHogPageviewUrl(window.location),
            ...(event
                ? {
                      $insert_id: event.event_id,
                  }
                : {}),
        });
    }, [
        location,
    ]);

    useEffect(() => {
        if (usage?.plan !== "pro") {
            return;
        }

        const hubSpotWindow = window as HubSpotWindow;
        const controller = new AbortController();

        const loadHubSpot = async () => {
            try {
                const authOrigin = import.meta.env.VITE_AUTH_URL || window.location.origin;
                const response = await fetch(new URL("/auth/hubspot/visitor-token", authOrigin), {
                    credentials: "include",
                    signal: controller.signal,
                });
                if (response.status === 401 || response.status === 403) {
                    return;
                }
                if (response.ok) {
                    const identification = (await response.json()) as {
                        email: string;
                        token: string;
                    };
                    hubSpotWindow.hsConversationsSettings = {
                        ...hubSpotWindow.hsConversationsSettings,
                        identificationEmail: identification.email,
                        identificationToken: identification.token,
                    };
                }
            } catch {
                // Identification is best effort; Pro users can still use anonymous support chat.
            }

            if (controller.signal.aborted) {
                return;
            }

            if (hubSpotWindow.HubSpotConversations) {
                const widget = hubSpotWindow.HubSpotConversations.widget;
                if (widget?.status?.().loaded) {
                    widget.refresh?.();
                } else {
                    widget?.load?.();
                }
            } else if (!document.getElementById(HUBSPOT_SCRIPT_ID)) {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.id = HUBSPOT_SCRIPT_ID;
                script.async = true;
                script.defer = true;
                script.src = HUBSPOT_SCRIPT_URL;
                document.body.appendChild(script);
            }
        };

        void loadHubSpot();

        return () => {
            controller.abort();
            hubSpotWindow.HubSpotConversations?.clear?.({
                resetWidget: true,
            });
            hubSpotWindow.HubSpotConversations?.widget?.remove?.();
            document.getElementById(HUBSPOT_SCRIPT_ID)?.remove();
            if (hubSpotWindow.hsConversationsSettings) {
                delete hubSpotWindow.hsConversationsSettings.identificationEmail;
                delete hubSpotWindow.hsConversationsSettings.identificationToken;
            }
        };
    }, [
        usage?.plan,
        user?.id,
    ]);

    return (
        <>
            <LegacyRedirectHandler />
            <Routes>
                {/* Root Route: Handles both Auth/Unauth states */}
                <Route path="/" element={<RootRoute />} />

                {/* Dashboard sections */}
                <Route
                    path="/usage"
                    element={
                        <RequireAuth>
                            <UsagePage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/billing"
                    element={
                        <RequireAuth>
                            <PlanBillingPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <RequireAuth>
                            <SettingsPage />
                        </RequireAuth>
                    }
                />

                <Route path="/device-verified" element={<DeviceVerifiedSuccessRoute />} />

                {/* Stripe redirects remain reachable even if the browser session expires during Checkout. */}
                <Route path="/payment-success" element={<BillingResultView success />} />
                <Route path="/payment-cancelled" element={<BillingResultView success={false} />} />

                {/* Connector OAuth Completion Flow */}
                <Route
                    path="/add-device"
                    element={
                        <RequireAuth>
                            <AddDevicePage />
                        </RequireAuth>
                    }
                />

                {/* Auth device verification flow */}
                <Route
                    path="/verify-device"
                    element={
                        <RequireAuth>
                            <DeviceVerificationPage />
                        </RequireAuth>
                    }
                />

                {/* Catch all - Redirect to Root */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
}

/** Reads the user's persisted light/dark choice and applies it to the Theme root. */
function ThemedApp() {
    const { appearance } = useThemeAppearance();

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <AuthProvider>
                    <AuthenticatedSupabaseProvider>
                        <UsageProvider>
                            <Theme appearance={appearance} accentColor="blue" grayColor="slate" hasBackground={false} radius="large" scaling="95%">
                                <AppRoutes />
                            </Theme>
                        </UsageProvider>
                    </AuthenticatedSupabaseProvider>
                </AuthProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

// Mount the application
ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <ThemeAppearanceProvider>
            <ThemedApp />
        </ThemeAppearanceProvider>
    </React.StrictMode>,
);

export default AppRoutes;
